// components/ButtonGroup/ButtonGroupComponent.js
import React, { useState, useEffect, useRef } from 'react';
import { ToggleButton, Box, Grid } from '@mui/material';
import gsap from 'gsap';
import PopupModalComponent from '../PopupModal/PopupModalComponent';
// import { toSentenceCase } from '../../utils/casing';

const ButtonGroupComponent = ({
  question,
  name,
  onChange,
  onNext,
  values,
  setValidationState,
  registerValidation,
  prop,
  isMobile,
}) => {
  // Start with no selection; do NOT hydrate from form values/question.value
  const [selected, setSelected] = useState('');
  const buttonRefs = useRef([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Reset selection on mount/when options change
  useEffect(() => {
    setSelected('');
    setValidationState?.(name, !question?.validation?.required);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, question?.options, question?.validation?.required]);

  const validate = (val = selected) => {
    const isValid = !!val && String(val).trim() !== '';
    setValidationState?.(name, isValid);
    return isValid;
  };

  // Register validation that uses local selected state only
  useEffect(() => {
    registerValidation?.(name, () => validate(selected));
  }, [name, selected, registerValidation]); // keep in sync

  const validateGroup = (fieldName, fieldValue) => {
    const group = prop?.questions?.find?.((g) =>
      Object.keys(g).includes(fieldName)
    );
    if (!group) return false;

    return Object.entries(group).every(([key]) => {
      if (key === 'layout') return true;
      const val = key === fieldName ? fieldValue : values?.[key];
      if (typeof val === 'string') return val.trim().length > 0;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === 'object' && val !== null)
        return Object.values(val).every((v) => v && v !== '');
      return val !== undefined && val !== null;
    });
  };

  const gs_bounce = (target, strength = 0.85) => {
    const tl = gsap.timeline();
    tl.to(target, { scale: strength, duration: 0.15, ease: 'power3.inOut' }).to(
      target,
      { scale: 1, duration: 1.3, ease: 'elastic.out(1.75, 0.4)' }
    );
    return tl;
  };

  const handleSelect = (option, index) => {
    const button = buttonRefs.current[index];
    if (button) gs_bounce(button);

    setSelected(option);
    // Lift state up, but do NOT persist to question.value (prevents rehydrate-on-reload)
    onChange?.(name, option);

    const isValid = validate(option);

    // If this option should open a popup, pass JSON to PopupModalComponent and stop flow
    const modalSpec = question?.onSelectModal?.[option];
    if (modalSpec) {
      setModalData(modalSpec);
      setModalOpen(true);
      return; // halt auto-next/branching; PopupModal handles the rest
    }

    if (
      isValid &&
      !question.disableAutoNext &&
      !prop.buttonLabel &&
      validateGroup(name, option)
    ) {
      onNext?.();
    }
  };

  const columnCount = question.columns || 2;
  const small = question.customSize === 'small';
  const mobileButtonWidth = 'calc(100vw - 162px)';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        mt: isMobile ? 0 : 0,
        overflow: 'visible',
      }}
    >
      <Grid
        container
        spacing={2}
        columns={columnCount}
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
          width: '100%',
          justifyContent: 'center',
          overflow: 'visible',
        }}
      >
        {question.options.map((option, index) => (
          <Grid
            item
            key={index}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <ToggleButton
              ref={(el) => (buttonRefs.current[index] = el)}
              value={option}
              selected={selected === option}
              onClick={() => handleSelect(option, index)}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.03em',
                textTransform: 'none',
                color: '#162F3B', // default text dark
                backgroundColor: '#E3E6E8', // default background grey
                border: '1px solid transparent',

                // Remove permanent green for selected
                '&.Mui-selected': {
                  backgroundColor: '#9EA900',
                  color: '#162F3B',
                  border: '1px solid transparent',
                },

                // Hover over selected = green + white
                '&.Mui-selected:hover': {
                  backgroundColor: '#C7CA00',
                  color: '#FFFFFF',
                  border: '1px solid #C7CA00',
                },

                // Hover over unselected = green + white
                '&:hover': {
                  backgroundColor: '#C7CA00',
                  color: '#FFFFFF',
                  border: '1px solid #C7CA00',
                },

                borderRadius: '18px',
                padding: small ? '12px 24px' : '15px 30px',
                width: isMobile ? mobileButtonWidth : small ? '216px' : '276px',
                maxWidth: '100%',
                height: '100%',
                minHeight: isMobile ? '52px' : small ? '46px' : '52px',
                boxSizing: 'border-box',
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                zIndex: 2,
                transformOrigin: 'center',
                willChange: 'transform',
                overflow: 'visible',
              }}
            >
              {option}
              {/* {toSentenceCase(option)} */}
            </ToggleButton>
          </Grid>
        ))}
      </Grid>

      {/* Guard: only render when we actually have modal data */}
      {modalOpen && modalData && (
        <PopupModalComponent
          data={modalData}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalData(null);
          }}
          onAction={() => {
            // PopupModal handles its own logic (incl. navigation).
            // We just close locally afterwards.
            setModalOpen(false);
            setModalData(null);
          }}
          isMobile={isMobile}
        />
      )}
    </Box>
  );
};

export default ButtonGroupComponent;
